import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ReferralPopup from '@src/components/ReferralPopup';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { TRANSPARENT_COLOR } from '@src/styles/color';
import {
  MAX_LENGTH_REFERRAL_CODE,
  REFERRAL_CODE_ALREADY_EXISTS,
} from '@src/constants/referral';
import { getValidateReferralCodeError } from '@src/services/referral';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import { StyledReferralDialogNote } from '../../index.style';
import { InputCount } from './InputCount';
import { StyledReferralRule } from './index.style';

const EditCodeDialog = ({ open, onClose, code, onChangeCode }) => {
  const { t } = useTranslation();
  const [referralCode, setReferralCode] = useState(code);
  const [error, setError] = useState({ key: '', params: {} });
  const isError = Boolean(error?.key);
  const dispatch = useDispatch();

  const handleInputChange = (e) => {
    const { value } = e.target;
    setReferralCode(value);
  };

  const checkValidateForm = (value) => {
    const errorRule = getValidateReferralCodeError(value);
    if (errorRule?.key) {
      setError(errorRule);
      return false;
    }

    setError({ key: '', params: {} });
    return true;
  };

  const notify = (message, severity) => {
    dispatch(actions.noti.push({ severity, message }));
  };

  const handleSave = async () => {
    const isValid = checkValidateForm(referralCode);
    if (isValid) {
      const response = await apis.referral.changeReferralCode(referralCode);
      const isSuccess = response.status;
      if (isSuccess) {
        onChangeCode(referralCode);
        notify(t('changeReferralCodeSuccess'), 'success');
        onClose();
      } else if (response.code === REFERRAL_CODE_ALREADY_EXISTS) {
        setError({ key: 'referralCodeAlreadyExists', params: {} });
      } else {
        notify(t('changeReferralCodeError'), 'error');
      }
    }
  };

  useEffect(() => {
    if (!open) {
      setReferralCode(code);
      setError({ key: '', params: {} });
    }
  }, [open]);

  const renderContent = () => (
    <>
      <InputCount
        name="referralCode"
        maxChar={MAX_LENGTH_REFERRAL_CODE}
        value={referralCode}
        onChange={handleInputChange}
        error={error}
      />
      {!isError && (
        <StyledReferralRule>
          <Typography className="rule-referral-code">
            {t('referralCodeValidateRules')}
          </Typography>
        </StyledReferralRule>
      )}
      <StyledReferralDialogNote backgroundColor={TRANSPARENT_COLOR.alert}>
        <WarningRoundedIcon fontSize="inherit" className="note-icon-warning" />
        <Typography className="note-text">
          {t('noteChangeReferralCode')}
        </Typography>
      </StyledReferralDialogNote>
    </>
  );

  const renderAction = () => (
    <>
      <Button
        variant="outlined"
        onClick={onClose}
        className="dialog-actions-button button-cancel"
      >
        {t('cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={!referralCode}
        className="dialog-actions-button button-save"
      >
        {t('saveWhenLeave')}
      </Button>
    </>
  );

  return (
    <ReferralPopup
      title={t('editReferralCode')}
      contentComponent={renderContent()}
      actionComponent={renderAction()}
      open={open}
      onClose={onClose}
    />
  );
};

export default EditCodeDialog;
